import { call, getContext, put, select, take, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { httpClientError, httpSuccess } from 'app/services/serviceHelpers';
import { MarginToolService } from 'app/services/MarginToolServices/margin-tool-service';
import {
  manuallyDecrementPromiseCounter,
  manuallyIncrementPromiseCounter,
  manuallyResetPromiseCounter,
  trackPromise,
} from 'react-promise-tracker';
import {
  GetGSAcalculatedValuesPayload,
  IAddScenarioRequestPayload,
} from '@AMIEWEB/MarginTool/Components/PayPackage/AddScenario/model';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { placementMarginScenarioActions } from './add-edit-placement-scenario.redux';
import {
  selectPlacementMarginBookingPeriod,
  selectPlacementMarginOverTimeHourlyRates,
  selectPlacementMarginToolDetailsData,
  selectPlacementMarginSearchPlacement,
  selectSelectedPlacementMarginScenario,
  selectPlacementMarginShiftDetails,
} from './add-edit-placement-scenario.selector';
import { selectUser } from 'oidc/user.selectors';
import {
  findMarginDetailByBookingPeriodId,
  getMarginOverTimeHourlyRatesFromScenario,
  updatedMarginToolDetails,
} from '@AMIEWEB/MarginTool/Components/PayPackage/AddScenario/helper';
import {
  IMarginUpdateResponse,
  IMarginUpdateResponseErrorTargetTypes,
  IMarginUpdateResponseErrorTypes,
} from '@AMIEWEB/MarginTool/Components/PayPackage/models/AddScenarioModal';
import {
  IDeletePlacementMarginToolDetailsPayload,
  IDeletePlacementMarginScenarioDetailsPayload,
  PlacementMarginStatusTransitionModalType,
} from '../placement-pay-package-status/placement-pay-package-status.model';
import i18next from 'i18next';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';
import { createPayloadMarginTool } from '@AMIEWEB/MarginTool/marginToolSavePayload';
import { IAssignmentForm } from '@AMIEWEB/MarginTool/Components/Assignments/models/IAssignmentSplitForm';
import { selectPlacementMarginEditTimeOffs } from '../placement-assignment/placement-assignment.selector';
import { getFormattedTimeOff } from '@AMIEWEB/MarginTool/Components/Assignments/RequestedTimeOff/EditCellRender/helper';
import { placementMarginPayPackageStatusActions } from '../placement-pay-package-status/placement-pay-package-status.redux';
import { IBannerAction } from 'app/models/Global/Global';
import {
  getDefaultShiftData,
  getDetailsByScenarioId,
  getSplitsDetails,
  getTlaShiftsPerWeek,
} from '@AMIEWEB/MarginTool/helper';
import { PayPackageOptions } from '@AMIEWEB/MarginTool/enum';
import { IPlacementMarginOverTimeHourlyRateItem } from './add-edit-placement-scenario.model';
import { formatTypeAheadOptions } from 'store/redux-store/margin-tool/helper';
import { getAutoSaveCalculatedScenarioValue } from '../placement-assignment/placement-assignment.saga';
import {
  getSplitsWithAdditionalPremium,
  getTotalReimbursement,
  roundToTwoDecimals,
  transformSplitsToPeopleSoftValues,
} from '@AMIEWEB/MarginTool/Components/PayPackage/helper';
import { IPlacementMarginCalculatedScenarioValue } from '../placement-assignment/placement-assignment.model';
import { PreviewContainerFieldNames } from '@AMIEWEB/MarginTool/Components/PayPackage/enum';
import moment from 'moment';
import { getExtensionDuration } from 'app/helpers/getExtensionDuration';
import { getRatesWeeklyCalculatedValue } from '@AMIEWEB/MarginTool/Components/PayPackage/PayPackagePreview/helper';

const TrackScenarioNavigationTreeData = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placementMarginTool.scenarioNavigationData);
const TrackSaveAddScenarioData = (fn, ...args) => trackPromise(fn(...args), 'save-add-scenario-modal');
const TrackUpsetScenarioData = (fn, ...args) => trackPromise(fn(...args), 'upsert-scenario-modal');
const TrackScenarioGSACalculateData = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placementMarginTool.gsaCalculateFetchData);
const TrackGetAddScenarioDetailsData = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placementMarginTool.getAddScenarioDetailsFetchData);
const TrackGetAddScenarioExtensionDetailsData = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placementMarginTool.getAddScenarioExtensionDetailsFetchData);
export const TrackSaveScenario = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placementMarginTool.saveScenario);

export function* requestAddPlacementMarginScenarioModalDetails(
  action: PayloadAction<{ placementId: number; bookingPeriodId?: number | null; isExtensionFlow: boolean }>,
) {
  try {
    yield put(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(null));
    yield put(globalActions.closeBanner());

    const marginToolService: MarginToolService = yield getContext('marginToolService');
    const { placementId, bookingPeriodId, isExtensionFlow } = action.payload;

    const response = yield call(
      TrackGetAddScenarioDetailsData,
      marginToolService.getMarginToolAddScenarioDetailsData,
      placementId, // Pass placementId
      bookingPeriodId, // Pass bookingPeriodId (can be undefined or null)
      isExtensionFlow, // Pass isExtensionFlow
    );

    if (httpSuccess(response.status)) {
      yield put(placementMarginScenarioActions.setAddPlacementMarginScenarioData(response?.data));
      const addScenario = response?.data;
      const selectionValue = addScenario;
      const shiftDetails = yield select(selectPlacementMarginShiftDetails);
      const defaultShiftData = getDefaultShiftData(
        shiftDetails, // Access shiftDetailsData from state
        selectionValue, // Access selectionValue from state
        PayPackageOptions.AddScenario, // Use 'addScenario' as the fixed modal name
      );
      const modelData = {
        extensionOfPriorPlacementID:
          addScenario?.isInExtension && addScenario?.isExtensionFromPriorPlacementId
            ? addScenario?.extensionOfPriorPlacementId?.toString()
            : null,
        notes: '',
        startDate: addScenario?.startDate,
        endDate: addScenario?.endDate,
        duration: addScenario?.duration ?? '',
        isInExtension: addScenario?.isInExtension,
        payCycleStartDay: addScenario?.payCycleStartDay,
        payCycleEndDay: addScenario?.payCycleEndDay,
        payCycleStartDayNum: addScenario?.payCycleStartDayNum,
        payCycleEndDayNum: addScenario?.payCycleEndDayNum,
        tla: addScenario?.tla,
        scenarioName: addScenario?.scenarioName,
        shiftDescription: addScenario?.shiftDescription,
        shiftId: addScenario?.shiftId,
        shift: defaultShiftData,
        hoursPerWeek: defaultShiftData?.object?.hoursPerWeek,
        hoursPerShift: defaultShiftData?.object?.hoursPerShift,
        splitsList: [
          {
            startDate: addScenario?.startDate,
            endDate: addScenario?.endDate,
            shift: defaultShiftData,
            hoursPerWeek: defaultShiftData?.object?.hoursPerWeek,
            hoursPerShift: defaultShiftData?.object?.hoursPerShift,
          },
        ],
      };
      yield put(placementMarginScenarioActions.setAddEditPlacementMarginScenarioModelData(modelData));
      yield put(
        placementMarginScenarioActions.setPlacementMarginScenarioModal({
          modalStatus: true,
          modalName: PayPackageOptions.AddScenario,
        }),
      );
    } else {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('global.apiResponseError'),
          severity: 'error',
        }),
      );
    }
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.getAddScenarioDetailsFetchData);
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.gsaCalculateFetchData);
  } catch (error) {
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.gsaCalculateFetchData);
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.getAddScenarioDetailsFetchData);
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'requestAddScenarioModalDetails',
        area: 'src/store/redux-store/margin-tool/marginTool.saga.ts',
      },
    });
  }
}

export function* requestAddPlacementMarginExtensionModalDetails(action: PayloadAction<{ placementId: number }>) {
  try {
    yield put(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(null));
    yield put(globalActions.closeBanner());
    const marginToolService: MarginToolService = yield getContext('marginToolService');
    const response = yield call(
      TrackGetAddScenarioExtensionDetailsData,
      marginToolService.getMarginToolAddExtentionDetailsData,
      action.payload.placementId,
    );
    if (httpSuccess(response.status)) {
      yield put(placementMarginScenarioActions.setAddPlacementMarginExtentionData(response?.data));
      const addExtension = response?.data;
      const selectionValue = addExtension;
      const shiftDetails = yield select(selectPlacementMarginShiftDetails);
      const defaultShiftData = getDefaultShiftData(
        shiftDetails, // Access shiftDetailsData from state
        selectionValue, // Access selectionValue from state
        PayPackageOptions.AddExtension, // Use 'AddExtension' as the fixed modal name
      );
      const modelData = {
        extensionOfPriorPlacementID: addExtension?.placementId?.toString(),
        notes: addExtension?.notes,
        startDate: addExtension?.startDate,
        endDate: addExtension?.endDate,
        duration: addExtension?.duration ?? '',
        isInExtension: addExtension?.isInExtension,
        tla: addExtension?.tla,
        scenarioName: 'Scenario 1', // Static scenario name
        shiftDescription: addExtension?.shiftDescription,
        shiftId: addExtension?.shiftId,
        shift: defaultShiftData,
        hoursPerWeek: addExtension?.hoursPerWeek,
        hoursPerShift: addExtension?.hoursPerShift,
        splitsList: [
          {
            startDate: addExtension?.startDate,
            endDate: addExtension?.endDate,
            shift: defaultShiftData,
            hoursPerWeek: defaultShiftData?.object?.hoursPerWeek,
            hoursPerShift: defaultShiftData?.object?.hoursPerShift,
          },
        ],
      };
      yield put(placementMarginScenarioActions.setAddEditPlacementMarginScenarioModelData(modelData));
      yield put(
        placementMarginScenarioActions.setPlacementMarginScenarioModal({
          modalStatus: true,
          modalName: PayPackageOptions.AddExtension,
        }),
      );
    } else {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('global.apiResponseError'),
          severity: 'error',
        }),
      );
    }
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.getAddScenarioExtensionDetailsFetchData);
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.gsaCalculateFetchData);
  } catch (error) {
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.getAddScenarioExtensionDetailsFetchData);
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.gsaCalculateFetchData);
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'requestAddExtentionModalDetails',
        area: 'src/store/redux-store/margin-tool/slices/add-edit-scenario/add-edit-scenario.saga.ts',
      },
    });
  }
}

export function* openPlacementMarginToolEditDetails(action: PayloadAction) {
  const selectedScenario = yield select(selectSelectedPlacementMarginScenario);
  const marginToolDetails = yield select(selectPlacementMarginToolDetailsData);
  const bookingPeriodDetails = yield select(selectPlacementMarginBookingPeriod);
  if (selectedScenario) {
    const selectedMarginToolDetails = selectedScenario?.scenarioId
      ? getDetailsByScenarioId(marginToolDetails, selectedScenario?.scenarioId)
      : null;
    const shiftDetails = yield select(selectPlacementMarginShiftDetails);
    const defaultEditScenarioDetails = selectedScenario?.splits[0];
    const defaultShiftData = getDefaultShiftData(
      shiftDetails, // Access shiftDetailsData from state
      defaultEditScenarioDetails, // Access selectionValue from state
      PayPackageOptions.editScenario, // Use 'AddExtension' as the fixed modal name
    );

    const modelData = {
      extensionOfPriorPlacementID: selectedMarginToolDetails?.isExtensionOfPriorPlacement
        ? selectedMarginToolDetails?.extensionOfPriorPlacementId?.toString()
        : '',
      notes: selectedScenario?.notes ?? '',
      startDate: bookingPeriodDetails?.bookingPeriodStartDate?.toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
      }),
      endDate: bookingPeriodDetails?.bookingPeriodEndDate?.toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
      }),
      duration: bookingPeriodDetails?.bookingPeriodDuration ?? '',
      isInExtension: selectedMarginToolDetails?.isExtensionOfPriorPlacement,
      tla: selectedScenario?.tla,
      scenarioName: selectedScenario?.scenarioName,
      shiftDescription: defaultShiftData?.object?.shiftDescription,
      shiftId: defaultShiftData?.object?.shiftId,
      shift: defaultShiftData,
      hoursPerWeek: defaultShiftData?.object?.hoursPerWeek,
      hoursPerShift: defaultShiftData?.object?.hoursPerShift,
      splitsList: getSplitsDetails(selectedScenario?.splits, shiftDetails),
      payCycleShiftType:bookingPeriodDetails?.payCycleShiftType,
      payCycleShiftDetails: bookingPeriodDetails?.payCycleShiftDetails,
    };
    yield put(placementMarginScenarioActions.setAddEditPlacementMarginScenarioModelData(modelData));
    yield put(placementMarginScenarioActions.setCurrentEditPlacementMarginScenarioDetails(modelData));
    yield put(
      placementMarginScenarioActions.setPlacementMarginScenarioModal({
        modalStatus: true,
        modalName: PayPackageOptions.editScenario,
      }),
    );
  }
}

export function* fetchPlacementMarginToolDetails(
  action: PayloadAction<{
    placementId: number;
    isDataUpdateFlow?: boolean | false | null;
    scenarioId?: number | null;
  }>,
) {
  try {
    const marginToolService: MarginToolService = yield getContext('marginToolService');
    const response = yield call(
      TrackScenarioNavigationTreeData,
      marginToolService.getMarginToolDetails,
      action.payload.placementId,
    );
    if (httpSuccess(response.status)) {
      const data = response?.data;
      const selectedPlacement = yield select(selectPlacementMarginSearchPlacement);
      const selectedMarginToolDetails = yield select(selectPlacementMarginToolDetailsData);
      const selectedScenarioExisting = yield select(selectSelectedPlacementMarginScenario);
      const updatedDetails = updatedMarginToolDetails(
        data,
        selectedMarginToolDetails,
        selectedPlacement,
        selectedScenarioExisting,
        action.payload.scenarioId,
      );
      yield put(placementMarginScenarioActions.setPlacementMarginGroupUpdateInprogressStatus(false));
      yield put(placementMarginScenarioActions.setPlacementMarginDetailResponse(updatedDetails));
      const selectedScenario = updatedDetails
        ?.map(item => item?.scenarios?.find(scenario => scenario.isDefaultScenario))
        ?.filter(Boolean)[0];
      yield put(placementMarginScenarioActions.setPlacementMarginScenario(selectedScenario));
      if (selectedScenario) {
        const peopleSoftValuesArray = transformSplitsToPeopleSoftValues(selectedScenario.splits);
        yield put(placementMarginScenarioActions.setPeopleSoftValues(peopleSoftValuesArray));
        const marginOverTimeHourlyRates = getMarginOverTimeHourlyRatesFromScenario(selectedScenario);

        yield put(placementMarginScenarioActions.setPlacementMarginOverTimeHourlyRates(marginOverTimeHourlyRates));

        const selectedBookingPeriod = updatedDetails?.find(item => item.scenarios.includes(selectedScenario));
        // To-do@Vipin - use one setter for setTreeViewBookingPeriod and render the placementId and bookingPeriodId conditionally
        if (selectedBookingPeriod) {
          yield put(placementMarginScenarioActions.setPlacementMarginBookingPeriod(selectedBookingPeriod));
          yield put(
            placementMarginScenarioActions.setPlacementMarginTreeViewBookingPeriod({
              placementId: selectedBookingPeriod.placementId,
              bookingPeriodId: selectedBookingPeriod.bookingPeriodId,
            }),
          );
        } else {
          yield put(
            placementMarginScenarioActions.setPlacementMarginTreeViewBookingPeriod({
              placementId: action.payload.placementId,
              bookingPeriodId: null,
            }),
          );
        }
      } else {
        yield put(placementMarginScenarioActions.setPlacementMarginOverTimeHourlyRates([]));
        yield put(
          placementMarginScenarioActions.setPlacementMarginTreeViewBookingPeriod({
            placementId: action.payload.placementId,
            bookingPeriodId: null,
          }),
        );
      }
      yield put(
        placementMarginScenarioActions.getPlacementMarginGSAcalculatedValues({
          autoSave: action.payload.isDataUpdateFlow ?? false,
        }),
      );

      //wait for GSA calculated values
      yield take(placementMarginScenarioActions.getPlacementMarginGSACalculatedValuesSuccess.type);

      if (action.payload.isDataUpdateFlow) {
        yield put(placementMarginScenarioActions.setFeatchPlacementMarginScenarioCalculate(true));
      }
    } else if (httpClientError(response.status)) {
      yield put(
        globalActions.setSnackBar({
          message: response?.data,
          severity: 'error',
        }),
      );
    } else {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('global.apiResponseError'),
          severity: 'error',
        }),
      );
      throw new Error(i18next.t('global.apiResponseError'));
    }
    yield put(placementMarginScenarioActions.setPlacementMarginPageLoadStatus(true));
  } catch (error) {
    yield put(placementMarginScenarioActions.setPlacementMarginPageLoadStatus(true));
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'fetchMarginToolDetails',
        area: 'src/store/redux-store/margin-tool/slices/add-edit-scenario/add-edit-scenario.saga.ts',
      },
    });
  }
}

export function* fetchPlacementMarginShiftData() {
  try {
    const marginToolService: MarginToolService = yield getContext('marginToolService');
    const response = yield call(marginToolService.getShiftData);
    if (httpSuccess(response.status)) {
      yield put(
        placementMarginScenarioActions.setPlacementMarginShiftDetailsData(formatTypeAheadOptions(response?.data)),
      );
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'fetchShiftData',
        area: 'src/store/redux-store/margin-tool/marginTool.saga.ts',
      },
    });
  }
}

export function* postAddPlacementMarginScenarioData(action: PayloadAction<IAddScenarioRequestPayload>) {
  try {
    yield put(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(null));
    yield put(globalActions.closeBanner());
    const marginToolService: MarginToolService = yield getContext('marginToolService');
    const response = yield call(TrackSaveAddScenarioData, marginToolService.postAddScenarioModal, action.payload);
    const scenarioResponse: IMarginUpdateResponse = response?.data;
    if (scenarioResponse) {
      scenarioResponse.target = IMarginUpdateResponseErrorTargetTypes.Model;
    }
    yield put(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(scenarioResponse));
    if (httpSuccess(response.status)) {
      if (!scenarioResponse?.inError) {
        yield put(
          globalActions.setSnackBar({
            message: scenarioResponse?.message,
            severity: 'success',
          }),
        );

        yield put(
          placementMarginScenarioActions.getPlacementMarginToolDetails({
            placementId: Number(action.payload.placementId),
            isDataUpdateFlow: true,
            scenarioId: scenarioResponse?.scenarioId,
          }),
        );

        yield put(
          placementMarginScenarioActions.setPlacementMarginScenarioModal({
            modalName: '',
            modalStatus: false,
          }),
        );

        yield put(placementMarginScenarioActions.setAddPlacementMarginScenarioData(null));
        yield put(placementMarginScenarioActions.setAddPlacementMarginExtentionData(null));
        yield put(placementMarginScenarioActions.setDeletedPlacementMarginScenarioSplits([]));
      } else {
        if (scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.BaseDetailsModifiedError) {
          yield put(placementMarginScenarioActions.setPlacementMarginIsOrderIdModifiedStatus(true));
          yield put(placementMarginScenarioActions.setPlacementMarginShowOrderIdModifiedDialogStatus(true));
        } else if (
          scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.Generic ||
          scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.ScenarioNameError
        ) {
          yield put(
            globalActions.setSnackBar({
              message: scenarioResponse?.message,
              severity: 'error',
            }),
          );
        }
      }
    } else {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('global.apiResponseError'),
          severity: 'error',
        }),
      );
    }
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('global.apiResponseError'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'postAddScenarioData',
        area: 'src/store/redux-store/margin-tool/marginTool.saga.ts',
      },
    });
  }
}

export function* updatePlacementMarginScenarioData(action: PayloadAction<IAddScenarioRequestPayload>) {
  try {
    yield put(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(null));
    yield put(globalActions.closeBanner());
    const marginToolService: MarginToolService = yield getContext('marginToolService');
    const response = yield call(TrackUpsetScenarioData, marginToolService.upsertScenarioDetails, action.payload);
    const scenarioResponse: IMarginUpdateResponse = response?.data;
    if (scenarioResponse) {
      scenarioResponse.target = IMarginUpdateResponseErrorTargetTypes.Model;
    }
    yield put(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(scenarioResponse));
    if (httpSuccess(response.status)) {
      //http 200
      if (!scenarioResponse?.inError) {
        //no error
        yield put(
          globalActions.setSnackBar({
            message: scenarioResponse?.message,
            severity: 'success',
          }),
        );
        yield put(
          placementMarginScenarioActions.getPlacementMarginToolDetails({
            placementId: Number(action.payload.placementId),
            isDataUpdateFlow: true,
          }),
        );
        yield put(
          placementMarginScenarioActions.setPlacementMarginScenarioModal({
            modalName: '',
            modalStatus: false,
          }),
        );

        yield put(placementMarginScenarioActions.setAddPlacementMarginScenarioData(null));
        yield put(placementMarginScenarioActions.setAddPlacementMarginExtentionData(null));
        yield put(placementMarginScenarioActions.setDeletedPlacementMarginScenarioSplits([]));
      } else {
        if (scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.BaseDetailsModifiedError) {
          yield put(placementMarginScenarioActions.setPlacementMarginIsOrderIdModifiedStatus(true));
          yield put(placementMarginScenarioActions.setPlacementMarginShowOrderIdModifiedDialogStatus(true));
        } else if (
          scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.Generic ||
          scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.ScenarioNameError
        ) {
          yield put(
            globalActions.setSnackBar({
              message: scenarioResponse?.message,
              severity: 'error',
            }),
          );
        }
      }
    } else {
      //http 400+
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('common.failedLoad'),
          severity: 'error',
        }),
      );
    }
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('common.failedLoad'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'updateScenarioData',
        area: 'src/store/redux-store/margin-tool/marginTool.saga.ts',
      },
    });
  }
}

export function* deleteSinglePlacementMarginScenario(
  action: PayloadAction<IDeletePlacementMarginScenarioDetailsPayload>,
) {
  try {
    yield put(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(null));
    yield put(globalActions.closeBanner());
    const marginToolService: MarginToolService = yield getContext('marginToolService');
    const response = yield call(marginToolService.deleteSingleScenario, action.payload);
    const scenarioResponse: IMarginUpdateResponse = response?.data;
    if (scenarioResponse) {
      scenarioResponse.target = IMarginUpdateResponseErrorTargetTypes.View;
    }
    yield put(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(scenarioResponse));
    if (httpSuccess(response.status)) {
      if (!scenarioResponse?.inError) {
        yield put(
          globalActions.setSnackBar({
            message: scenarioResponse?.message,
            severity: 'success',
          }),
        );
        // Fetch the updated margin tool details after successful deletion
        const selectedScenario = yield select(selectSelectedPlacementMarginScenario);
        const treeViewData = yield select(selectPlacementMarginToolDetailsData);
        const { scenarioId } = selectedScenario;

        const updatedTreeViewData = treeViewData.map(bookingPeriod => {
          if (bookingPeriod.bookingPeriodId === selectedScenario.bookingPeriodId) {
            // Filter out the scenario with the specified scenarioId
            const updatedScenarios = bookingPeriod.scenarios.filter(scenario => scenario.scenarioId !== scenarioId);

            // If no scenarios are left, set bookingPeriodId to null
            if (updatedScenarios.length === 0) {
              return {
                ...bookingPeriod,
                bookingPeriodId: null,
                scenarios: [], // Ensure scenarios is an empty array
              };
            }

            // Otherwise, return the booking period with the updated scenarios
            return {
              ...bookingPeriod,
              scenarios: updatedScenarios,
            };
          }

          // Return the booking period as-is if bookingPeriodId does not match
          return bookingPeriod;
        });

        yield put(placementMarginScenarioActions.setPlacementMarginDetailResponse(updatedTreeViewData));

        //yield call({ type: 'fetchMarginToolDetails', payload: { placementId: selectedScenario.placementId } });
        yield put(
          placementMarginScenarioActions.getPlacementMarginToolDetails({ placementId: selectedScenario.placementId }),
        );
        /*const treeData = yield select(selectTreeViewData);
        const updatedTreeData = getUpdatedTreeData(action.payload.scenarioId, treeData);
        //yield put(scenarioActions.setScenarioTreeData(updatedTreeData));
        yield put(scenarioActions.setMarginDetailResponse(updatedTreeData));
        if (!updatedTreeData?.length) {
          yield put(scenarioActions.setScenario(null));
          yield put(scenarioActions.setBookingPeriod(null));
        }*/
      } else {
        if (scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.TimeStampError) {
          yield put(
            globalActions.setBanner({
              message: scenarioResponse?.message || i18next.t('common.failedLoad'),
              severity: 'error',
              justify: 'flex-start',
              action: IBannerAction.refreshPage,
              justifyActionContent: 'flex-end',
            }),
          );
        } else {
          yield put(
            globalActions.setSnackBar({
              message: scenarioResponse?.message || i18next.t('common.failedLoad'),
              severity: 'error',
            }),
          );
        }
      }
    } else {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('common.failedLoad'),
          severity: 'error',
        }),
      );
    }
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('common.failedLoad'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'deleteSingleScenario',
        area: 'src/store/redux-store/margin-tool/marginTool.saga.ts',
      },
    });
  }
}

export function* cleanPlacementMarginToolDetails(action: PayloadAction<IDeletePlacementMarginToolDetailsPayload>) {
  try {
    yield put(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(null));
    yield put(globalActions.closeBanner());
    const marginToolService: MarginToolService = yield getContext('marginToolService');
    const response = yield call(marginToolService.deleteMarginDetails, action.payload);
    if (httpSuccess(response.status)) {
      if (response?.data) {
        yield put(placementMarginScenarioActions.setPlacementMarginShowOrderIdModifiedDialogStatus(false));
        yield put(placementMarginScenarioActions.reset());
        yield put(placementMarginScenarioActions.getPlacementMarginShiftDetails());
        yield put(
          placementMarginScenarioActions.getPlacementMarginToolDetails({ placementId: action.payload.placementId }),
        );
        yield put(
          globalActions.setSnackBar({
            message: i18next.t('marginTool.orderIdModifiedConfirmation.commonSucessMessage'),
            severity: 'success',
          }),
        );
      } else {
        yield put(
          globalActions.setSnackBar({
            message: i18next.t('marginTool.orderIdModifiedConfirmation.commonErrorMessage'),
            severity: 'error',
          }),
        );
      }
    } else if (httpClientError(response.status)) {
      yield put(
        globalActions.setSnackBar({
          message: response?.data || i18next.t('global.apiResponseError'),
          severity: 'error',
        }),
      );
    } else {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('global.apiResponseError'),
          severity: 'error',
        }),
      );
      throw new Error(i18next.t('global.apiResponseError'));
    }
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('common.failedLoad'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'deleteSingleScenario',
        area: 'src/store/redux-store/margin-tool/marginTool.saga.ts',
      },
    });
  }
}

/**
 * Method to get GSA values for a selected margin detail and selected scenario booking period
 */
export function* getPlacementMarginGSAcalculatedValues(action: PayloadAction<GetGSAcalculatedValuesPayload>) {
  try {
    yield put(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(null));
    yield put(globalActions.closeBanner());

    const marginToolService: MarginToolService = yield getContext('marginToolService');
    const currentUser = yield select(selectUser);
    const marginDetails = yield select(selectPlacementMarginToolDetailsData);
    const selectedScenario = yield select(selectSelectedPlacementMarginScenario);
    if (marginDetails?.length && selectedScenario?.bookingPeriodId > 0) {
      const selectedMarginDetail = findMarginDetailByBookingPeriodId(marginDetails, selectedScenario.bookingPeriodId);
      if (selectedMarginDetail) {
        const { autoSave = false } = action.payload;
        const requestPayload = {
          bookingPeriodId: selectedMarginDetail.bookingPeriodId,
          facilityId: selectedMarginDetail?.facility?.facilityId,
          startDate: selectedMarginDetail?.bookingPeriodStartDate,
          endDate: selectedMarginDetail?.bookingPeriodEndDate,
          userId: currentUser?.userInfo?.employeeId,
          autoSave,
        };
        const response = yield call(
          TrackScenarioGSACalculateData,
          marginToolService.getGSAcalculatedValues,
          requestPayload,
        );
        yield put(
          placementMarginScenarioActions.setPlacementMarginGSAcalculatedValues(
            httpSuccess(response?.status) ? response?.data : null,
          ),
        );
      }
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'getGSAcalculatedValues',
        area: 'src/store/redux-store/margin-tool/marginTool.saga.ts',
      },
    });
  }
  yield put(placementMarginScenarioActions.getPlacementMarginGSACalculatedValuesSuccess());
}

/**
 * MarginTool Save Scenario Saga - Save button from the footer
 */
export function* postPlacementMarginToolSaveData(
  action: PayloadAction<{
    formData: IAssignmentForm;
    loadDetails: boolean | true;
    isApprovalFlow?: boolean | false;
    resolve?: Function;
    reject?: Function;
  }>,
) {
  try {
    manuallyIncrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.saveScenario);
    // Clear previous response state
    yield put(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(null));
    yield put(globalActions.closeBanner());

    // Get services and state
    const marginToolService: MarginToolService = yield getContext('marginToolService');
    const assignmentForm = action.payload.formData;
    const loadDetails = action.payload.loadDetails;
    const isApprovalFlow = action.payload.isApprovalFlow ?? false;
    const currentUser = yield select(selectUser);
    const selectedScenario = yield select(selectSelectedPlacementMarginScenario);
    const editTimeOffs = yield select(selectPlacementMarginEditTimeOffs);
    const timeOffs = getFormattedTimeOff(editTimeOffs);
    const existingRates: IPlacementMarginOverTimeHourlyRateItem[] = yield select(
      selectPlacementMarginOverTimeHourlyRates,
    );

    // Create payload
    const transformedPayload = createPayloadMarginTool(
      assignmentForm,
      selectedScenario,
      currentUser,
      selectedScenario?.timestamp,
      timeOffs,
      isApprovalFlow,
      existingRates,
    );

    const bookingData = yield select(selectPlacementMarginBookingPeriod);
    const headerData = yield select(selectSelectedPlacementMarginScenario);
    const tlaEnabled = headerData?.tla;
    const brand = bookingData?.brand;
    const payPackageDetailsValues = transformedPayload?.assignment?.payPackageDetailsValues;
    const splits = transformedPayload.splits;

    const placementMarginCalculatedSplitValue = [];
    let splitsWithAdditionalPremiumPay = getSplitsWithAdditionalPremium(splits, payPackageDetailsValues) || [];
    let scnarioWeeklyGross = splitsWithAdditionalPremiumPay?.reduce((acc, item) => {
      const duration = getExtensionDuration(moment(item?.effectiveStartDate), item?.effectiveEndDate);
      let totalWeeklyGrossItem = 0;
      const hoursPerWeek = item?.hoursPerWeek;
      const hoursPerShift = item?.hoursPerShift;

      const currSplitRates = existingRates?.find(x => x?.splitId === item?.splitId);

      let payRateCalculatedValue = getRatesWeeklyCalculatedValue({
        fieldName: PreviewContainerFieldNames.payRate,
        currSplitRates,
        eachSplit: item,
        hoursPerWeek: item?.hoursPerWeek,
      });
      let overtimeCalculatedValue = getRatesWeeklyCalculatedValue({
        fieldName: PreviewContainerFieldNames.overTimeRate,
        currSplitRates,
        eachSplit: item,
        hoursPerWeek: item?.hoursPerWeek,
      });
      let premiumPayCalculatedValue = getRatesWeeklyCalculatedValue({
        fieldName: PreviewContainerFieldNames.premiumPayRate,
        currSplitRates,
        eachSplit: item,
        hoursPerWeek: item?.hoursPerWeek,
      });
      let doubletimeCalculatedValue = getRatesWeeklyCalculatedValue({
        fieldName: PreviewContainerFieldNames.doubletimeRate,
        currSplitRates,
        eachSplit: item,
        hoursPerWeek: item?.hoursPerWeek,
      });

      if (payRateCalculatedValue) {
        totalWeeklyGrossItem += payRateCalculatedValue;
      }
      if (overtimeCalculatedValue) {
        totalWeeklyGrossItem += overtimeCalculatedValue;
      }
      if (premiumPayCalculatedValue) {
        totalWeeklyGrossItem += premiumPayCalculatedValue;
      }
      if (doubletimeCalculatedValue) {
        totalWeeklyGrossItem += doubletimeCalculatedValue;
      }
      if (tlaEnabled) {
        const tlaShiftsPerWeek = getTlaShiftsPerWeek(hoursPerWeek, hoursPerShift);
        if (item?.hasOwnProperty(PreviewContainerFieldNames.tlaCarAllowance)) {
          const carAllowance =
            roundToTwoDecimals(item[PreviewContainerFieldNames.tlaCarAllowance]) * tlaShiftsPerWeek;
          totalWeeklyGrossItem += carAllowance;
        }
        if (item?.hasOwnProperty(PreviewContainerFieldNames.tlaMealAllowance)) {
          const mealAllowance =
            roundToTwoDecimals(item[PreviewContainerFieldNames.tlaMealAllowance]) * tlaShiftsPerWeek;
          totalWeeklyGrossItem += mealAllowance;
        }
        if (item?.hasOwnProperty(PreviewContainerFieldNames.tlaCellPhoneStipend)) {
          const cellPhoneStipend =
            roundToTwoDecimals(item[PreviewContainerFieldNames.tlaCellPhoneStipend]) * tlaShiftsPerWeek;
          totalWeeklyGrossItem += cellPhoneStipend;
        }
        if (item?.hasOwnProperty(PreviewContainerFieldNames.tlaShiftCompletionBonus)) {
          const shiftCompletionBonus =
            roundToTwoDecimals(item[PreviewContainerFieldNames.tlaShiftCompletionBonus]) * tlaShiftsPerWeek;
          totalWeeklyGrossItem += shiftCompletionBonus;
        }
      } else {
        if (item?.hasOwnProperty(PreviewContainerFieldNames.mealPerDiem)) {
          totalWeeklyGrossItem += roundToTwoDecimals(item[PreviewContainerFieldNames.mealPerDiem]) * 7;
        }
        if (item?.hasOwnProperty(PreviewContainerFieldNames.lodgingPerDiem)) {
          totalWeeklyGrossItem += roundToTwoDecimals(item[PreviewContainerFieldNames.lodgingPerDiem]) * 7;
        }
        if (item?.hasOwnProperty(PreviewContainerFieldNames.cellPhoneStipend)) {
          totalWeeklyGrossItem += roundToTwoDecimals(item[PreviewContainerFieldNames.cellPhoneStipend]) * 7;
        }
      }
      placementMarginCalculatedSplitValue.push({
        splitId: item?.splitId,
        weeklyGrossPay: totalWeeklyGrossItem?.toFixed(2),
      });
      const weeklyTotal = totalWeeklyGrossItem * duration;
      return acc + weeklyTotal;
    }, 0);
    const totalReimbursement = getTotalReimbursement(transformedPayload, brand);
    const placementMarginCalculatedScenarioValue: IPlacementMarginCalculatedScenarioValue = {
      weeklyGrossPay: (scnarioWeeklyGross + totalReimbursement)?.toFixed(2),
      splits: placementMarginCalculatedSplitValue,
    };

    transformedPayload.weeklyGrossPay = placementMarginCalculatedScenarioValue.weeklyGrossPay;
    placementMarginCalculatedScenarioValue.splits?.forEach(ctxSplit => {
      const target = transformedPayload.splits?.find(s => s.splitId === ctxSplit.splitId);
      if (target) {
        target.weeklyGrossPay = ctxSplit.weeklyGrossPay;
      }
    });

    // Call the API
    const response = yield call(marginToolService.postMarginToolSave, transformedPayload);
    const scenarioResponse: IMarginUpdateResponse = response?.data;

    // Set additional fields if needed
    if (scenarioResponse) {
      scenarioResponse.target = IMarginUpdateResponseErrorTargetTypes.View;
    }

    // Update the state with the response
    yield put(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(scenarioResponse));

    // Handle success and error
    if (httpSuccess(response.status)) {
      if (!scenarioResponse?.inError) {
        if (loadDetails) {
          // Fetch updated margin tool details if needed
          yield call(fetchPlacementMarginToolDetails, {
            payload: {
              placementId: Number(selectedScenario?.placementId),
              isDataUpdateFlow: false,
            },
            type: placementMarginScenarioActions.getPlacementMarginToolDetails.type,
          });
        }

        // Resolve the promise if it exists
        if (action.payload.resolve) {
          action.payload.resolve({ data: scenarioResponse });
        }
        manuallyDecrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.saveScenario);
        yield put(
          globalActions.setSnackBar({
            message: scenarioResponse?.message,
            severity: 'success',
          }),
        );
      } else if (scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.BaseDetailsModifiedError) {
        yield put(placementMarginScenarioActions.setPlacementMarginIsOrderIdModifiedStatus(true));
        yield put(placementMarginScenarioActions.setPlacementMarginShowOrderIdModifiedDialogStatus(true));
      } else if (
        scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.TimeStampError ||
        scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.PerDiemError
      ) {
        yield put(
          placementMarginPayPackageStatusActions.setPayPackageStatusTransitionModalDetails({
            openModal: false,
            type: PlacementMarginStatusTransitionModalType.None,
          }),
        );
        if (scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.TimeStampError) {
          yield put(
            globalActions.setBanner({
              message: scenarioResponse?.message || i18next.t('common.failedLoad'),
              severity: 'error',
              justify: 'flex-start',
              action: IBannerAction.refreshPage,
              justifyActionContent: 'flex-end',
            }),
          );
        } else if (scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.PerDiemError) {
          yield put(
            globalActions.setBanner({
              message: scenarioResponse?.message || i18next.t('common.failedLoad'),
              severity: 'error',
              justify: 'flex-start',
            }),
          );
        }
      } else {
        yield put(
          globalActions.setSnackBar({
            message: scenarioResponse?.message || i18next.t('common.failedLoad'),
            severity: 'error',
          }),
        );
      }

      // Reject the promise if it exists
      if (action.payload.reject) {
        manuallyDecrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.saveScenario);
        action.payload.reject({ error: scenarioResponse });
      }
    } else {
      manuallyDecrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.saveScenario);
      // Handle HTTP response errors
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('common.failedLoad'),
          severity: 'error',
        }),
      );

      // Reject the promise if it exists
      if (action.payload.reject) {
        action.payload.reject({ error: 'HTTP error' });
      }
    }
    manuallyDecrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.saveScenario);
    yield put(placementMarginScenarioActions.setSavePlacementMarginTriggeredStatus(false));
    yield put(placementMarginScenarioActions.setCancelPlacementMarginTriggeredStatus(false));
  } catch (error) {
    yield put(placementMarginScenarioActions.setSavePlacementMarginTriggeredStatus(false));
    yield put(placementMarginScenarioActions.setCancelPlacementMarginTriggeredStatus(false));
    manuallyDecrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.saveScenario);
    // Handle unexpected errors
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('common.failedLoad'),
        severity: 'error',
      }),
    );

    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'postMarginToolSaveData',
        area: 'src/store/redux-store/margin-tool/marginTool.saga.ts',
      },
    });

    // Reject the promise if it exists
    if (action.payload.reject) {
      action.payload.reject({ error });
    }
  }
}

export function* postPlacementMarginToolDetailsValidate(
  action: PayloadAction<{
    formData: IAssignmentForm;
    resolve?: Function;
    reject?: Function;
  }>,
) {
  try {
    manuallyIncrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.validateScenario);
    // Clear previous response state
    yield put(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(null));
    yield put(globalActions.closeBanner());

    // Get services and state
    const marginToolService: MarginToolService = yield getContext('marginToolService');
    const assignmentForm = action.payload.formData;
    const isApprovalFlow = true;
    const currentUser = yield select(selectUser);
    const selectedScenario = yield select(selectSelectedPlacementMarginScenario);
    const editTimeOffs = yield select(selectPlacementMarginEditTimeOffs);
    const timeOffs = getFormattedTimeOff(editTimeOffs);

    // Create payload
    const transformedPayload = createPayloadMarginTool(
      assignmentForm,
      selectedScenario,
      currentUser,
      selectedScenario?.timestamp,
      timeOffs,
      isApprovalFlow,
    );

    // Call the API
    const response = yield call(marginToolService.postMarginToolValidate, transformedPayload);
    const scenarioResponse: IMarginUpdateResponse = response?.data;

    // Set additional fields if needed
    if (scenarioResponse) {
      scenarioResponse.target = IMarginUpdateResponseErrorTargetTypes.View;
    }

    // Update the state with the response
    yield put(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(scenarioResponse));

    // Handle success and error
    if (httpSuccess(response.status)) {
      if (!scenarioResponse?.inError) {
        // Resolve the promise if it exists
        if (action.payload.resolve) {
          manuallyDecrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.validateScenario);
          action.payload.resolve({ data: scenarioResponse });
        }
      } else if (scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.BaseDetailsModifiedError) {
        yield put(placementMarginScenarioActions.setPlacementMarginIsOrderIdModifiedStatus(true));
        yield put(placementMarginScenarioActions.setPlacementMarginShowOrderIdModifiedDialogStatus(true));
      } else if (
        scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.TimeStampError ||
        scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.PerDiemError
      ) {
        yield put(
          placementMarginPayPackageStatusActions.setPayPackageStatusTransitionModalDetails({
            openModal: false,
            type: PlacementMarginStatusTransitionModalType.None,
          }),
        );
        if (scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.TimeStampError) {
          yield put(
            globalActions.setBanner({
              message: scenarioResponse?.message || i18next.t('common.failedLoad'),
              severity: 'error',
              justify: 'flex-start',
              action: IBannerAction.refreshPage,
              justifyActionContent: 'flex-end',
            }),
          );
        } else if (scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.PerDiemError) {
          yield put(
            globalActions.setBanner({
              message: scenarioResponse?.message || i18next.t('common.failedLoad'),
              severity: 'error',
              justify: 'flex-start',
            }),
          );
        }
      } else {
        yield put(
          globalActions.setSnackBar({
            message: scenarioResponse?.message || i18next.t('common.failedLoad'),
            severity: 'error',
          }),
        );
      }

      // Reject the promise if it exists
      if (action.payload.reject) {
        manuallyDecrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.validateScenario);
        action.payload.reject({ error: scenarioResponse });
      }
    } else {
      manuallyDecrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.validateScenario);
      // Handle HTTP response errors
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('common.failedLoad'),
          severity: 'error',
        }),
      );

      // Reject the promise if it exists
      if (action.payload.reject) {
        action.payload.reject({ error: 'HTTP error' });
      }
    }
    manuallyDecrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.validateScenario);
  } catch (error) {
    manuallyDecrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.validateScenario);
    // Handle unexpected errors
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('common.failedLoad'),
        severity: 'error',
      }),
    );

    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'postMarginToolDetailsValidate',
        area: 'src/store/redux-store/margin-tool/marginTool.saga.ts',
      },
    });

    // Reject the promise if it exists
    if (action.payload.reject) {
      action.payload.reject({ error });
    }
  }
}

export function* placementMarginScenarioSaga() {
  yield takeLatest(
    placementMarginScenarioActions.getAddPlacementMarginScenarioDetails,
    requestAddPlacementMarginScenarioModalDetails,
  );
  yield takeLatest(
    placementMarginScenarioActions.getAddPlacementMarginExtensionDetails,
    requestAddPlacementMarginExtensionModalDetails,
  );
  yield takeLatest(placementMarginScenarioActions.getPlacementMarginToolDetails, fetchPlacementMarginToolDetails);
  yield takeLatest(placementMarginScenarioActions.getPlacementMarginShiftDetails, fetchPlacementMarginShiftData);
  yield takeLatest(
    placementMarginScenarioActions.postAddPlacementMarginScenarioDetails,
    postAddPlacementMarginScenarioData,
  );
  yield takeLatest(
    placementMarginScenarioActions.postEditPlacementMarginScenarioDetails,
    updatePlacementMarginScenarioData,
  );
  yield takeLatest(placementMarginScenarioActions.postPlacementMarginToolDetails, postPlacementMarginToolSaveData);
  yield takeLatest(
    placementMarginScenarioActions.postPlacementMarginToolDetailsValidate,
    postPlacementMarginToolDetailsValidate,
  );
  yield takeLatest(
    placementMarginScenarioActions.deleteSinglePlacementMarginScenario,
    deleteSinglePlacementMarginScenario,
  );
  yield takeLatest(
    placementMarginScenarioActions.getPlacementMarginGSAcalculatedValues,
    getPlacementMarginGSAcalculatedValues,
  );
  yield takeLatest(placementMarginScenarioActions.cleanPlacementMarginToolDetails, cleanPlacementMarginToolDetails);
  yield takeLatest(
    placementMarginScenarioActions.openPlacementMarginToolEditDetails,
    openPlacementMarginToolEditDetails,
  );
}
